import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import TileModal from './TileModal';
import { Icons } from '../lib/Icons';

/**
 * Display teaching plan activities as tiles with score breakdown
 * Each tile opens a modal with detailed how-to, watchouts, and resources
 */
const DISPLAY_BANDS = [
  { min: 70, label: 'Strong', className: 'text-green-800 bg-green-100' },
  { min: 55, label: 'Good', className: 'text-blue-800 bg-blue-100' },
  { min: 40, label: 'Fair', className: 'text-amber-800 bg-amber-100' },
];

const TeachingPlanTiles = ({ activities, studentName, hideTitle = false }) => {
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState({});

  const cohortMaxScore = Math.max(...(activities || []).map((a) => a.score || 0), 1);

  const toDisplayScore = (raw) => Math.round((100 * (raw || 0)) / cohortMaxScore);

  const displayBand = (displayScore) => {
    const band = DISPLAY_BANDS.find((b) => displayScore >= b.min);
    return band || { label: 'Exploratory', className: 'text-gray-700 bg-gray-100' };
  };

  const toggleScoreBreakdown = (activityId) => {
    setShowScoreBreakdown(prev => ({
      ...prev,
      [activityId]: !prev[activityId]
    }));
  };

  const renderScoreBreakdown = (breakdown) => {
    if (!breakdown) return null;

    const scoreItems = [
      { label: 'Need Match', value: breakdown.exactNeed, max: 40, color: 'text-blue-600' },
      { label: 'Hierarchy', value: breakdown.hierNeed, max: 10, color: 'text-blue-500' },
      { label: 'Communication', value: breakdown.comms, max: 10, color: 'text-green-600' },
      { label: 'Attention', value: breakdown.attention, max: 10, color: 'text-yellow-600' },
      { label: 'SEMH', value: breakdown.semh, max: 8, color: 'text-pink-600' },
      { label: 'Sensory', value: breakdown.sensory, max: 8, color: 'text-purple-600' },
      { label: 'Attainment', value: breakdown.attainment, max: 6, color: 'text-indigo-600' },
      { label: 'Resources', value: breakdown.resources, max: 4, color: 'text-orange-600' },
      { label: 'Novelty', value: breakdown.novelty, max: 4, color: breakdown.novelty < 0 ? 'text-red-600' : 'text-green-600' },
      { label: 'Domain Gap', value: breakdown.domainGap, max: 4, color: 'text-teal-600' }
    ];

    return (
      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm font-medium text-gray-700 mb-2">
          Match Score: {breakdown.total}/100
        </div>
        <div className="space-y-1">
          {scoreItems.map((item, idx) => (
            <div key={idx} className="flex justify-between items-center text-xs">
              <span className="text-gray-600">{item.label}:</span>
              <span className={`font-medium ${item.color}`}>
                {item.value > 0 ? '+' : ''}{item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!activities || activities.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <p>No activities matched for this student.</p>
            <p className="text-sm mt-2">
              If you just set up the emulator, run <code className="text-xs bg-gray-100 px-1 rounded">npm run seed:kb</code> from
              the repo root, then use Regenerate Plan. Otherwise, complete the classroom questionnaire with more detail or
              update the student profile.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Domain icon mapping
  const getDomainIconComponent = (domain) => {
    const iconMap = {
      'Communication': Icons.ChatBubble,
      'Cognition': Icons.Brain,
      'SEMH': Icons.Heart,
      'Sensory/Physical': Icons.Hand,
      'Sensory': Icons.Hand,
      'Physical': Icons.Hand,
    };
    
    return iconMap[domain] || Icons.BookOpen;
  };

  // Domain color mapping
  const getDomainColor = (domain) => {
    const colors = {
      'Communication': 'bg-blue-100 text-blue-800',
      'Cognition': 'bg-purple-100 text-purple-800',
      'SEMH': 'bg-pink-100 text-pink-800',
      'Sensory/Physical': 'bg-green-100 text-green-800',
      'Sensory': 'bg-green-100 text-green-800',
      'Physical': 'bg-yellow-100 text-yellow-800',
    };
    
    return colors[domain] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        {!hideTitle && (
          <h2 className="text-2xl font-bold">Teaching Plan for {studentName}</h2>
        )}
        <div className={`text-sm text-gray-500 ${hideTitle ? 'ml-auto' : ''}`}>
          {activities.length} {activities.length === 1 ? 'activity' : 'activities'} matched
          <span className="block text-xs text-gray-400">
            Match score is relative fit for this pupil&apos;s profile (normalized to top match).
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activities.map((activity, index) => (
          <Card
            key={index}
            className="hover:shadow-lg transition-shadow border-2"
          >
            <CardContent className="p-4">
              {/* Title */}
              <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                {activity.title}
              </h3>
              {activity.mergedAliases?.length > 0 && (
                <p className="text-xs text-gray-500 mb-2">
                  Also known as: {activity.mergedAliases.join(', ')}
                </p>
              )}

              {/* Domain tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                {activity.targetDomains?.map((domain, idx) => (
                  <span
                    key={idx}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getDomainColor(domain)}`}
                  >
                    <span>{getDomainIconComponent(domain)}</span>
                    <span>{domain}</span>
                  </span>
                ))}
              </div>

              {/* Brief description */}
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {activity.description}
              </p>

              {/* Frequency indicator */}
              {activity.recommendedFrequency && (
                <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                  Suggested: {activity.recommendedFrequency.perWeek}× per week, {activity.recommendedFrequency.sessionMinutes} min
                </div>
              )}

              {/* Match score and breakdown */}
              {activity.score !== undefined && (
                <div className="mt-2">
                  {(() => {
                    const displayScore = toDisplayScore(activity.score);
                    const band = displayBand(displayScore);
                    return (
                      <>
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded mb-1 ${band.className}`}>
                          {band.label} match
                        </span>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Match score:</span>
                          <span className="text-xs font-medium text-gray-700">
                            {displayScore}/100
                            <span className="text-gray-400 ml-1">(raw {activity.score})</span>
                          </span>
                        </div>
                      </>
                    );
                  })()}
                  
                  {/* Score breakdown toggle */}
                  <button
                    onClick={() => {
                      toggleScoreBreakdown(activity.kbItemId || index);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 mt-1"
                  >
                    {showScoreBreakdown[activity.kbItemId || index] ? 'Hide' : 'Show'} breakdown
                  </button>
                  
                  {/* Score breakdown details */}
                  {showScoreBreakdown[activity.kbItemId || index] && 
                   renderScoreBreakdown(activity.breakdown)}
                </div>
              )}

              {/* More Details button */}
              <button
                onClick={() => {
                  console.log('More Details clicked for:', activity.title);
                  setSelectedActivity(activity);
                }}
                className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                More Details
              </button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Modal for selected activity */}
      {selectedActivity && (
        <TileModal
          activity={selectedActivity}
          isOpen={!!selectedActivity}
          onClose={() => {
            console.log('Closing modal');
            setSelectedActivity(null);
          }}
        />
      )}
      
      {/* Debug info */}
      {selectedActivity && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 p-2 rounded text-xs">
          Modal should be open for: {selectedActivity.title}
        </div>
      )}
    </div>
  );
};

export default TeachingPlanTiles;

